import { CollapseProps, Collapse, Flex, Divider, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { ContractorApiListSharedType } from '../../types';
import { FolderOutlined } from '@ant-design/icons';
import { UnshareDocumentButton } from './unshare-document-button';
import { useApi } from '../../api/useApi';
import { Category, Document } from '../../interfaces/documents';
import { getListDataAfterShareItemsDelete } from './get-list-data-after-share-items-delete';
import { getRecipientContact } from './get-recipient-contact';

interface MySharedDocumentsTabByRecipientProps {
    listData: ContractorApiListSharedType;
    setUpdateData: React.Dispatch<React.SetStateAction<boolean>>;
}

export const MySharedDocumentsTabByRecipient: React.FC<MySharedDocumentsTabByRecipientProps> = ({
    listData,
    setUpdateData,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [internalListData, setInternalListData] = useState<ContractorApiListSharedType>(listData);

    const api = useApi();

    useEffect(() => {
        setInternalListData(listData);
    }, [listData]);

    const getDivider = (caption: string) => (
        <Divider size={'small'} orientation="left" style={{ fontSize: 12, fontWeight: 600 }}>
            {caption}
        </Divider>
    );

    const stopShare =
        (typeStop: 'recipient' | 'resource' | 'recipient-resource', recipientId?: string, resourceId?: string) =>
        async () => {
            setIsLoading(true);
            const result = await api.documents.stopShare({
                typeStop,
                recipientId,
                resourceId,
            });
            setUpdateData(true);
            console.log(result);

            const deletedListShareItem = resourceId
                ? internalListData.listShareItem.filter(
                      (shareItem) => shareItem.rid === recipientId && shareItem.id === resourceId,
                  )
                : internalListData.listShareItem.filter((shareItem) => shareItem.rid === recipientId);

            setInternalListData(getListDataAfterShareItemsDelete(internalListData, deletedListShareItem));

            setIsLoading(false);
        };

    const items: CollapseProps['items'] = [...internalListData.listRecipient]
        .sort((a, b) => getRecipientContact(a).localeCompare(getRecipientContact(b)))
        ?.flatMap((recipient) => {
            const filterDocumentFolder = (entity: Document | Category) =>
                !!internalListData.listShareItem.find(
                    (shareItem) =>
                        shareItem.rid === recipient.id &&
                        shareItem.id === ((entity as Category).uuid || (entity as Document).id),
                );

            const filteredListFolder = internalListData.listFolder.filter(filterDocumentFolder);
            const filteredListDocument = internalListData.listDocument.filter(filterDocumentFolder);

            if (!filteredListFolder.length && !filteredListDocument.length) {
                return [];
            }

            return [
                {
                    key: recipient.id,
                    label: (
                        <Flex justify={'space-between'}>
                            <span>{getRecipientContact(recipient)}</span>
                            <UnshareDocumentButton onClick={stopShare('recipient', recipient.id)} />
                        </Flex>
                    ),
                    children: (
                        <Flex vertical>
                            {!!filteredListFolder.length && (
                                <>
                                    {getDivider('Folders')}
                                    {filteredListFolder.map((folder) => (
                                        <Flex justify={'space-between'} style={{ margin: '2px 0' }}>
                                            <Flex gap={10}>
                                                <FolderOutlined />
                                                {folder.name}
                                            </Flex>
                                            <UnshareDocumentButton
                                                onClick={stopShare('recipient-resource', recipient.id, folder.uuid)}
                                            />
                                        </Flex>
                                    ))}
                                </>
                            )}
                            {!!filteredListDocument.length && (
                                <>
                                    {getDivider('Documents')}
                                    {filteredListDocument.map((document) => (
                                        <Flex justify={'space-between'} style={{ margin: '2px 0', marginLeft: 24 }}>
                                            <Flex>{`${document.name} ${document.extension}`}</Flex>
                                            <UnshareDocumentButton
                                                onClick={stopShare('recipient-resource', recipient.id, document.id)}
                                            />
                                        </Flex>
                                    ))}
                                </>
                            )}
                        </Flex>
                    ),
                },
            ];
        });

    return <Spin spinning={isLoading}>{<Collapse ghost items={items} />}</Spin>;
};
